import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../config/api';
import AreaOptions from '../components/AreaOptions';
import Pagination, { DEFAULT_PAGE_SIZE, pagingParams } from '../components/Pagination';
import {
  EMERGENCY_CATEGORY,
  DEPARTMENT_HINTS,
  HELP_CATEGORIES,
  formatWhen,
  helpCategoryLabel,
} from '../constants/help';
import { apiFetch, getSessionStatus } from '../utils/session';
import './HelpRequest.css';

function AidRecord({ aid }) {
  if (!aid?.type) return null;
  const when = aid.date
    ? new Date(aid.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  return (
    <p className="help-meta">
      Aid: {aid.type}
      {aid.type === 'money' && aid.amount != null ? ` · ₹${aid.amount}` : ''}
      {when ? ` · ${when}` : ''}
      {aid.note ? ` · ${aid.note}` : ''}
    </p>
  );
}

function RequestCard({ item, onFollowUp, onClose, busy }) {
  const [message, setMessage] = useState('');
  const away = item.helpArea !== item.homeArea;
  const canFollowUp = item.status !== 'Closed' && !item.followUp;

  return (
    <article className="help-card">
      <span className="help-status" data-status={item.status}>
        {item.status}
      </span>
      <h2 className="help-card-title">{helpCategoryLabel(item.category)}</h2>
      <p className="help-meta">
        Help in {item.helpArea}
        {away ? ` · Home area ${item.homeArea}` : ''}
        {item.createdAt ? ` · ${formatWhen(item.createdAt)}` : ''}
      </p>
      <p className="help-body">{item.description}</p>
      {item.helper?.name ? (
        <p className="help-meta">
          Helper: {item.helper.name}
          {item.helper.memberId ? ` · ${item.helper.memberId}` : ''}
        </p>
      ) : null}
      <AidRecord aid={item.aid} />
      {item.followUp ? (
        <p className="help-meta">
          Your follow-up: {item.followUp}
          {item.followUpAt ? ` · ${formatWhen(item.followUpAt)}` : ''}
        </p>
      ) : null}
      {item.statusHistory?.length ? (
        <ol className="help-history">
          {item.statusHistory.map((entry, index) => (
            <li key={`${entry.status}-${entry.at}-${index}`}>
              {entry.status}
              {entry.actorName ? ` · ${entry.actorName}` : ''}
              {entry.at ? ` · ${formatWhen(entry.at)}` : ''}
            </li>
          ))}
        </ol>
      ) : null}
      {canFollowUp ? (
        <form
          className="help-field"
          onSubmit={(e) => {
            e.preventDefault();
            onFollowUp(item._id, message);
          }}
        >
          <label htmlFor={`follow-${item._id}`}>One follow-up</label>
          <textarea
            id={`follow-${item._id}`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={1000}
            required
          />
          <button type="submit" className="help-btn" disabled={busy}>
            Send follow-up
          </button>
        </form>
      ) : null}
      {item.status !== 'Closed' ? (
        <button type="button" className="help-btn" disabled={busy} onClick={() => onClose(item._id)}>
          Close request
        </button>
      ) : null}
    </article>
  );
}

export default function HelpRequest() {
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [helping, setHelping] = useState([]);
  const [category, setCategory] = useState('employment');
  const [helpArea, setHelpArea] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const approved = getSessionStatus() === 'Active';

  const load = useCallback(async () => {
    setError('');
    const [profileRes, listRes, helpingRes] = await Promise.all([
      apiFetch(apiUrl('/profile/me')),
      apiFetch(apiUrl(`/help/mine?${pagingParams(page, pageSize)}`)),
      apiFetch(apiUrl('/help/helping')),
    ]);
    const profileData = await profileRes.json().catch(() => ({}));
    const listData = await listRes.json().catch(() => ({}));
    const helpingData = await helpingRes.json().catch(() => ({}));
    if (!profileRes.ok) throw new Error(profileData.error || 'Could not load your profile');
    if (!listRes.ok) throw new Error(listData.error || 'Could not load help requests');
    if (!helpingRes.ok) throw new Error(helpingData.error || 'Could not load requests you are helping with');
    setProfile(profileData.profile);
    setHelping(helpingData.requests || []);
    setHelpArea((current) => current || profileData.profile.area || '');
    setRequests(listData.requests || []);
    setTotal(listData.total ?? (listData.requests || []).length);
  }, [page, pageSize]);

  useEffect(() => {
    if (!approved) {
      setLoading(false);
      return;
    }
    load()
      .catch((e) => setError(e.message || 'Could not load help requests'))
      .finally(() => setLoading(false));
  }, [approved, load]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    if (category === EMERGENCY_CATEGORY) return;
    if (!profile?.area) {
      setError('Your profile has no area set. Update your profile before requesting help.');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch(apiUrl('/help'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, description, helpArea: selectedArea }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not send help request');
        return;
      }
      setCategory('employment');
      setDescription('');
      setHelpArea(profile?.area || '');
      setNotice(
        data.request.helpArea === data.request.homeArea
          ? `Request sent to the ${data.request.helpArea} Area Head.`
          : `Request sent to the ${data.request.helpArea} Area Head. Your home Area Head can read it.`
      );
      setPage(1);
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const followUp = async (id, message) => {
    setSaving(true);
    setError('');
    try {
      const res = await apiFetch(apiUrl(`/help/${id}/follow-up`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not add follow-up');
        return;
      }
      setNotice('Follow-up sent.');
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const closeRequest = async (id) => {
    if (!window.confirm('Close this help request?')) return;
    setSaving(true);
    setError('');
    try {
      const res = await apiFetch(apiUrl(`/help/${id}/close`), { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not close request');
        return;
      }
      setNotice('Request closed.');
      await load();
    } catch {
      setError('Network error. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const selectedArea = helpArea || profile?.area || '';
  const away = Boolean(profile?.area && selectedArea && selectedArea !== profile.area);

  return (
    <div className="help-page">
      <div className="help-shell">
        <header className="help-header">
          <div>
            <h1 className="help-title">Need help</h1>
            <p className="help-sub">मुझे सहायता चाहिए. This is not the emergency SOS button.</p>
          </div>
        </header>

        {!approved ? (
          <p className="help-error" role="alert">
            Help requests are available after your membership is approved.
          </p>
        ) : null}

        {error ? (
          <p className="help-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? (
          <div className="help-notice" role="status">
            <p className="help-notice-text">{notice}</p>
            <button
              type="button"
              className="help-notice-dismiss"
              aria-label="Dismiss"
              onClick={() => setNotice('')}
            >
              ×
            </button>
          </div>
        ) : null}

        {approved && profile ? (
          <form className="help-form" onSubmit={submit}>
            <label className="help-field">
              What do you need?
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {HELP_CATEGORIES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
                <option value={EMERGENCY_CATEGORY}>Emergency</option>
              </select>
            </label>
            {DEPARTMENT_HINTS[category] ? <p className="help-hint">{DEPARTMENT_HINTS[category]}</p> : null}

            {category === EMERGENCY_CATEGORY ? (
              <div className="help-emergency" role="alert">
                <p>
                  For a medical or safety emergency, call <a href="tel:112">112</a> first. Then press and hold
                  the red SOS button. This form does not send emergency alerts.
                </p>
              </div>
            ) : (
              <>
                {!profile.area ? (
                  <p className="help-error" role="alert">
                    Your profile has no area set. Update your profile before requesting help.{' '}
                    <Link to="/profile">Go to profile</Link>
                  </p>
                ) : (
                  <>
                    <label className="help-field">
                      Where do you need help?
                      <select value={selectedArea} onChange={(e) => setHelpArea(e.target.value)} required>
                        <AreaOptions current={selectedArea} />
                      </select>
                    </label>
                    <p className="help-hint">
                      {away
                        ? `Your home area is ${profile.area}. The ${selectedArea} Area Head will handle this request. Your home Area Head can read it and cannot change it.`
                        : `Your home area is ${profile.area}. The request goes to your Area Head.`}
                    </p>
                    <label className="help-field">
                      What happened?
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        maxLength={2000}
                        required
                      />
                    </label>
                    <button type="submit" className="help-btn primary" disabled={saving || !profile.area}>
                      {saving ? 'Sending…' : 'Send request'}
                    </button>
                  </>
                )}
              </>
            )}
          </form>
        ) : null}

        {approved && helping.length ? (
          <section>
            <h2 className="help-card-title">Requests you are helping with</h2>
            <ul className="help-list">
              {helping.map((item) => (
                <li key={item._id}>
                  <article className="help-card">
                    <span className="help-status" data-status={item.status}>
                      {item.status}
                    </span>
                    <h2 className="help-card-title">{helpCategoryLabel(item.category)}</h2>
                    <p className="help-meta">Help in {item.helpArea}</p>
                    <p className="help-body">{item.description}</p>
                    <p className="help-meta">Phone: {item.member?.phone || 'No phone on profile'}</p>
                    <AidRecord aid={item.aid} />
                  </article>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {approved ? (
          <section>
            <h2 className="help-card-title">Your requests</h2>
            {loading ? <p className="help-muted">Loading…</p> : null}
            {!loading && requests.length === 0 ? (
              <p className="help-muted">You have not asked for help yet.</p>
            ) : null}
            <ul className="help-list">
              {requests.map((item) => (
                <li key={item._id}>
                  <RequestCard item={item} busy={saving} onFollowUp={followUp} onClose={closeRequest} />
                </li>
              ))}
            </ul>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </section>
        ) : null}
      </div>
    </div>
  );
}
